import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Container, Typography, TextField, Button, Box, Paper, IconButton, 
  Divider, FormControlLabel, Switch, MenuItem, Select, InputLabel, FormControl, 
  Stack, Grid, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, Tooltip, InputAdornment, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from '../../api/axiosConfig'; 
import { useNavigate, useParams } from 'react-router-dom';

// --- 비디오 미리보기 컴포넌트 (검은 화면 해결 핵심 수정) ---
const VideoPreview = ({ src }) => {
  const videoRef = useRef(null);

  // 마우스 오버 시 재생 로직
  const handleMouseEnter = () => { if (videoRef.current && src) videoRef.current.play().catch(() => {}); };
  const handleMouseLeave = () => { if (videoRef.current && src) { videoRef.current.pause(); videoRef.current.currentTime = 0.5; } };

  return (
    <Box 
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      sx={{ 
        width: 110, height: 65, borderRadius: 1.5, overflow: 'hidden', 
        backgroundColor: '#1a1a1a', border: '1px solid #ddd', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      {src ? (
        <video
          // [핵심] key에 src를 주면, 주소가 바뀔 때마다 비디오 태그 자체가 새로 고쳐져서 검은 화면이 사라집니다.
          key={src} 
          ref={videoRef}
          src={src + "#t=0.5"}
          width="100%" height="100%"
          muted playsInline
          // 브라우저에게 비디오 로드 의지를 강하게 전달합니다.
          preload="auto"
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          style={{ objectFit: 'cover' }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: '#555', fontSize: '0.7rem' }}>NO VIDEO</Typography>
      )}
    </Box>
  );
};

const QuestionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const THEME_NAVY = '#1a233a';
  const THEME_ORANGE = '#ed6c02';

  const [categories, setCategories] = useState([]);
  const [masterVideos, setMasterVideos] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]); 
  const [typeCodes, setTypeCodes] = useState([]);   
  const [groupCodes, setGroupCodes] = useState([]); 
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false); 
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterGroup, setFilterGroup] = useState('ALL');
  const [targetSlot, setTargetSlot] = useState(null); 

  const [questionData, setQuestionData] = useState({
    categoryPk: '',
    title: '',       
    question: '',    
    videoPk: '',     
    videoTitle: '', 
    videoUrl: '',    
    description: '', 
    sortApplYn: 'N',
    score: 0,        
    options: []
  });

  const fetchInitialData = useCallback(async () => {
    try {
      setInitialLoading(true);
      const [catRes, groupRes, typeRes, qRes, detailRes] = await Promise.all([
        axios.get('/api/category'),
        axios.get('/api/common-code/group/00001'),
        axios.get('/api/common-code/group/00002'),
        axios.get('/api/question'),
        axios.get(`/api/question/${id}`)
      ]);
      setCategories(catRes.data);
      setGroupCodes(groupRes.data || []);
      setTypeCodes(typeRes.data || []);
      setAllQuestions(qRes.data || []);
      setQuestionData(detailRes.data);
    } catch (err) { 
      console.error("데이터 로딩 실패:", err); 
      alert("데이터를 불러오는데 실패했습니다.");
    } finally {
      setInitialLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

  const fetchVideos = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get('/api/video', {
        params: {
          search: filterText,
          group: filterGroup === 'ALL' ? '' : filterGroup,
          type: filterType === 'ALL' ? '' : filterType
        }
      });
      setMasterVideos(response.data || []);
    } catch (err) {
      console.error("비디오 로딩 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const categoryStatus = useMemo(() => {
    if (!questionData.categoryPk) return { current: 0, expected: 0 };
    const currentSum = allQuestions
      .filter(q => q.categoryPk === questionData.categoryPk && q.questionPk !== Number(id))
      .reduce((sum, q) => sum + (Number(q.score) || 0), 0);
    return {
      current: currentSum,
      expected: currentSum + (Number(questionData.score) || 0)
    };
  }, [allQuestions, questionData.categoryPk, questionData.score, id]);

  const handleVideoSelect = (video) => {
    if (targetSlot === 'main') {
      setQuestionData(prev => ({ ...prev, videoPk: video.videoPk, videoTitle: video.title, videoUrl: video.videoUrl }));
    } else if (typeof targetSlot === 'number') {
      const newOptions = [...questionData.options];
      newOptions[targetSlot] = { ...newOptions[targetSlot], videoPk: video.videoPk, videoTitle: video.title, videoUrl: video.videoUrl };
      setQuestionData(prev => ({ ...prev, options: newOptions }));
    }
    setTargetSlot(null);
  };

  const handleClearVideoOnly = (e, target, index = null) => {
    e.stopPropagation();
    if (target === 'main') {
      setQuestionData(prev => ({ ...prev, videoPk: '', videoTitle: '', videoUrl: '' }));
    } else {
      const newOptions = [...questionData.options];
      newOptions[index] = { ...newOptions[index], videoPk: '', videoTitle: '', videoUrl: '' };
      setQuestionData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const handleCancelSelection = (e) => {
    e.stopPropagation();
    setTargetSlot(null);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...questionData.options];
    newOptions[index][field] = value;
    setQuestionData({ ...questionData, options: newOptions });
  };

  const handleAddOption = () => {
    setQuestionData({
      ...questionData,
      options: [...questionData.options, { videoPk: '', videoTitle: '', videoUrl: '', optionText: '', correctYn: 'N', sortIndex: questionData.options.length + 1 }]
    });
  };

  const handleRemoveOptionSlot = (e, index) => {
    e.stopPropagation();
    const newOptions = questionData.options.filter((_, i) => i !== index);
    setQuestionData({ ...questionData, options: newOptions.map((opt, i) => ({ ...opt, sortIndex: i + 1 })) });
  };

  const handleSubmit = async () => {
    if (!questionData.categoryPk || !questionData.title) return alert("필수 항목을 확인하세요.");
    const hasCorrectOption = questionData.options.some(opt => opt.correctYn === 'Y');
    if (!hasCorrectOption) return alert("보기에 정답이 최소 1개는 설정되어야 합니다.");

    if (categoryStatus.expected > 100) {
      if (!window.confirm(`이 카테고리의 총점이 100점을 초과(${categoryStatus.expected}점)합니다. 그래도 저장하시겠습니까?`)) return;
    }

    const payload = {
      categoryPk: questionData.categoryPk,
      title: questionData.title,
      question: questionData.question,
      score: questionData.score, 
      videoPk: questionData.videoPk || null,
      description: questionData.description,
      sortApplYn: questionData.sortApplYn,
      options: questionData.options.map(opt => ({
        videoPk: opt.videoPk || null,
        optionText: opt.optionText,
        correctYn: opt.correctYn,
        sortIndex: opt.sortIndex
      }))
    };

    try {
      await axios.put(`/api/question/${id}`, payload);
      alert('성공적으로 수정되었습니다.');
      navigate(-1);
    } catch (err) { alert('수정 중 오류 발생'); }
  };

  if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 800, color: THEME_NAVY }}>문제 수정 엔진</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, borderTop: `6px solid ${THEME_NAVY}`, bgcolor: '#fdfdfd' }}>
            <Stack spacing={4}>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 'bold' }}>카테고리</InputLabel>
                  <Select value={questionData.categoryPk} label="카테고리" onChange={(e) => setQuestionData({...questionData, categoryPk: e.target.value})}>
                    {categories.map((cat) => <MenuItem key={cat.categoryPk} value={cat.categoryPk}>{cat.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={2}>
                  <TextField label="문제 배점" type="number" sx={{ width: '150px' }} size="small" value={questionData.score} onChange={(e) => setQuestionData({...questionData, score: e.target.value})} />
                  {questionData.categoryPk && (
                    <Box sx={{ 
                      flexGrow: 1, p: 1, borderRadius: 1, border: '1px solid', display: 'flex', alignItems: 'center', gap: 1,
                      bgcolor: categoryStatus.expected === 100 ? '#e8f5e9' : categoryStatus.expected > 100 ? '#ffebee' : '#f0f4f8',
                      borderColor: categoryStatus.expected === 100 ? '#4caf50' : categoryStatus.expected > 100 ? '#f44336' : '#d1d9e0'
                    }}>
                      {categoryStatus.expected === 100 ? <CheckCircleOutlineIcon size="small" color="success" /> : 
                       categoryStatus.expected > 100 ? <ErrorOutlineIcon size="small" color="error" /> : <InfoOutlinedIcon size="small" color="primary" />}
                      <Typography variant="caption" fontWeight="bold">카테고리 총합: {categoryStatus.expected}/100</Typography>
                    </Box>
                  )}
                </Stack>
                <TextField label="관리용 제목" fullWidth size="small" value={questionData.title} onChange={(e) => setQuestionData({...questionData, title: e.target.value})} />
                <TextField label="문제 지문 (Question)" multiline rows={2} fullWidth value={questionData.question} onChange={(e) => setQuestionData({...questionData, question: e.target.value})} />
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: THEME_NAVY }}>대표 영상</Typography>
                <Paper onClick={() => setTargetSlot('main')} sx={{ p: 2, cursor: 'pointer', border: targetSlot === 'main' ? `2px solid ${THEME_ORANGE}` : '1px solid #e0e0e0', bgcolor: targetSlot === 'main' ? '#fff9f2' : '#fff' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <VideoPreview src={questionData.videoUrl} />
                    <TextField fullWidth size="small" placeholder="우측에서 영상을 선택하세요" value={questionData.videoTitle || (questionData.videoPk ? `연결됨 (ID: ${questionData.videoPk})` : "")} InputProps={{ readOnly: true }} />
                    {targetSlot === 'main' ? (
                      <Tooltip title="선택 취소"><IconButton color="warning" onClick={handleCancelSelection}><CloseIcon /></IconButton></Tooltip>
                    ) : (
                      <Tooltip title="영상 연결 해제"><IconButton color="error" onClick={(e) => handleClearVideoOnly(e, 'main')}><DeleteIcon /></IconButton></Tooltip>
                    )}
                  </Stack>
                </Paper>
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>보기(Options) 구성</Typography>
                <FormControlLabel control={<Switch checked={questionData.sortApplYn === 'Y'} onChange={(e) => setQuestionData({...questionData, sortApplYn: e.target.checked ? 'Y' : 'N'})} />} label="순서 적용" />
              </Box>

              <Stack spacing={3}>
                {questionData.options.map((option, index) => (
                  <Paper 
                    key={index} elevation={0} onClick={() => setTargetSlot(index)}
                    sx={{ p: 2.5, border: targetSlot === index ? `2px solid ${THEME_ORANGE}` : '1px solid #eee', borderLeft: option.correctYn === 'Y' ? '8px solid #2e7d32' : '8px solid #e0e0e0', bgcolor: targetSlot === index ? '#fff9f2' : '#fff' }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 900, color: THEME_NAVY }}>OPTION {index + 1}</Typography>
                        <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={(e) => handleRemoveOptionSlot(e, index)} sx={{ borderRadius: 2, fontSize: '0.75rem' }}>보기 삭제</Button>
                      </Stack>
                      <TextField label="보기 텍스트 지문" fullWidth size="small" value={option.optionText} onClick={(e) => e.stopPropagation()} onChange={(e) => handleOptionChange(index, 'optionText', e.target.value)} />
                      <Stack direction="row" spacing={2} alignItems="center">
                        <VideoPreview src={option.videoUrl} />
                        <TextField fullWidth size="small" placeholder="연결된 영상 제목" value={option.videoTitle || (option.videoPk ? `연결됨 (ID: ${option.videoPk})` : "")} InputProps={{ readOnly: true }} sx={{ flexGrow: 1 }} />
                        <Stack direction="row" spacing={1}>
                          <Button variant={option.correctYn === 'Y' ? "contained" : "outlined"} color="success" size="small" sx={{ height: 40, minWidth: 60 }} onClick={(e) => { e.stopPropagation(); handleOptionChange(index, 'correctYn', option.correctYn === 'Y' ? 'N' : 'Y'); }}>정답</Button>
                          {targetSlot === index ? (
                            <Tooltip title="선택 취소"><IconButton color="warning" sx={{ border: '1px solid #ffb74d', borderRadius: 1 }} onClick={handleCancelSelection}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                          ) : (
                            <Tooltip title="영상만 삭제"><IconButton color="warning" sx={{ border: '1px solid #ffb74d', borderRadius: 1 }} onClick={(e) => handleClearVideoOnly(e, 'option', index)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
              <Button startIcon={<AddIcon />} onClick={handleAddOption} variant="outlined" fullWidth sx={{ borderStyle: 'dashed', py: 1.5 }}>보기 추가</Button>
              <TextField label="해설" multiline rows={2} fullWidth value={questionData.description} onChange={(e) => setQuestionData({...questionData, description: e.target.value})} />
              <Button variant="contained" size="large" onClick={handleSubmit} sx={{ bgcolor: THEME_NAVY, py: 2, fontSize: '1.1rem' }}>수정 내용 저장</Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper elevation={3} sx={{ borderRadius: 3, display: 'flex', flexDirection: 'column', height: '88vh', position: 'sticky', top: 20, overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, bgcolor: THEME_NAVY, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <VideoLibraryIcon />
                <Typography variant="h6" fontWeight="bold">비디오 라이브러리</Typography>
              </Stack>
              {targetSlot !== null && <Chip label={`입력 중: ${targetSlot === 'main' ? '대표영상' : (targetSlot+1)+'번 보기'}`} color="warning" variant="filled" onDelete={() => setTargetSlot(null)} sx={{ fontWeight: 'bold' }} />}
            </Box>

            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
              <Stack spacing={1.5}>
                <TextField 
                  fullWidth size="small" placeholder="영상 제목 또는 태그 검색..." value={filterText} 
                  onChange={(e) => setFilterText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchVideos()}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ bgcolor: '#fff' }} 
                />
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>그룹 필터</InputLabel>
                    <Select label="그룹 필터" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} sx={{ bgcolor: '#fff' }}>
                      <MenuItem value="ALL">전체 그룹</MenuItem>
                      {groupCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>유형 필터</InputLabel>
                    <Select label="유형 필터" value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ bgcolor: '#fff' }}>
                      <MenuItem value="ALL">전체 유형</MenuItem>
                      {typeCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button 
                    variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} 
                    onClick={fetchVideos} sx={{ bgcolor: THEME_NAVY, minWidth: '100px' }}
                  >
                    조회
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <TableContainer sx={{ flexGrow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f4f6f8', fontWeight: 'bold', py: 1.5 } }}>
                    <TableCell align="center" width="120">미리보기</TableCell>
                    <TableCell>영상 정보</TableCell>
                    <TableCell align="center" width="100">분류</TableCell>
                    <TableCell align="center" width="80">선택</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                  ) : !hasSearched ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 15 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">조회 조건을 설정한 후 조회 버튼을 클릭해주세요.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : masterVideos.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}>검색 결과가 없습니다.</TableCell></TableRow>
                  ) : (
                    masterVideos.map((row) => (
                      <TableRow key={row.videoPk} hover sx={{ cursor: targetSlot !== null ? 'pointer' : 'default', '&:hover': { bgcolor: '#f1f4f8' } }} onClick={() => targetSlot !== null && handleVideoSelect(row)}>
                        <TableCell align="center"><VideoPreview src={row.videoUrl} /></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: THEME_NAVY }}>{row.title}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">ID: {row.videoPk}</Typography>
                          {row.tags && <Typography variant="caption" sx={{ color: THEME_ORANGE, fontSize: '0.7rem' }}>#{row.tags}</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Stack spacing={0.5} alignItems="center">
                            <Chip label={groupCodes.find(c => c.detailCode === row.videoGroup)?.detailCodeName || row.videoGroup} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8eaed', fontWeight: 'bold' }} />
                            <Chip label={typeCodes.find(c => c.detailCode === row.videoType)?.detailCodeName || row.videoType} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Button variant="contained" size="small" disabled={targetSlot === null} sx={{ borderRadius: 4, minWidth: 50, fontSize: '0.75rem', boxShadow: 'none' }}>선택</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1, textAlign: 'right', bgcolor: '#f8f9fa' }}>
              <Typography variant="caption" color="text.secondary">Total: {masterVideos.length}건</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default QuestionEdit;